import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import {
  FlaskConical, LogOut, FileText, Package, BarChart3, MoreHorizontal,
  Eye, Trash2, ArrowRight, RefreshCw, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  new: 'bg-blue-100 text-blue-800 border-blue-200',
  reviewed: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  quoted: 'bg-green-100 text-green-800 border-green-200',
  closed: 'bg-gray-100 text-gray-600 border-gray-200',
};

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="p-6 border border-[#DEE2E6]" data-testid={`stat-${label.toLowerCase().replace(/ /g, '-')}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] tracking-widest uppercase text-[#A0AAB2]" style={{ fontFamily: 'var(--font-mono)' }}>
          {label}
        </span>
        <Icon className="w-4 h-4 text-[#A0AAB2]" strokeWidth={1.5} />
      </div>
      <span className="text-3xl text-[#0A1128]" style={{ fontFamily: 'var(--font-heading)', fontWeight: 700 }}>
        {value}
      </span>
    </div>
  );
}

function RFQDetailModal({ rfq, onClose, onStatusChange }) {
  if (!rfq) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="rfq-detail-modal">
        <div className="p-8 border-b border-[#DEE2E6]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl tracking-tight text-[#0A1128]" style={{ fontFamily: 'var(--font-heading)', fontWeight: 700 }}>
              RFQ Detail
            </h3>
            <Badge className={`rounded-none border ${statusColors[rfq.status] || statusColors.new}`} variant="outline" style={{ fontFamily: 'var(--font-mono)' }}>
              {rfq.status}
            </Badge>
          </div>
          <div className="grid grid-cols-2 gap-6">
            {[
              { label: 'Company', value: rfq.company_name },
              { label: 'Contact', value: rfq.contact_name },
              { label: 'Email', value: rfq.email },
              { label: 'Phone', value: rfq.phone || 'N/A' },
              { label: 'Country', value: rfq.country || 'N/A' },
              { label: 'Product', value: rfq.product_interest },
              { label: 'Intended Use', value: rfq.intended_use },
              { label: 'Volume', value: rfq.volume_required },
            ].map(item => (
              <div key={item.label}>
                <span className="text-[10px] tracking-widest uppercase text-[#A0AAB2] block mb-0.5" style={{ fontFamily: 'var(--font-mono)' }}>{item.label}</span>
                <span className="text-sm text-[#0A1128]" style={{ fontFamily: 'var(--font-body)' }}>{item.value}</span>
              </div>
            ))}
          </div>
          {rfq.additional_notes && (
            <div className="mt-6">
              <span className="text-[10px] tracking-widest uppercase text-[#A0AAB2] block mb-1" style={{ fontFamily: 'var(--font-mono)' }}>Notes</span>
              <p className="text-sm text-[#495057] bg-[#F8F9FA] p-4 border border-[#DEE2E6]" style={{ fontFamily: 'var(--font-body)' }}>
                {rfq.additional_notes}
              </p>
            </div>
          )}
          <div className="mt-6">
            <span className="text-[10px] tracking-widest uppercase text-[#A0AAB2] block mb-1" style={{ fontFamily: 'var(--font-mono)' }}>Submitted</span>
            <span className="text-sm text-[#0A1128]" style={{ fontFamily: 'var(--font-mono)' }}>
              {rfq.created_at ? new Date(rfq.created_at).toLocaleString() : 'N/A'}
            </span>
          </div>
        </div>
        <div className="p-6 flex items-center justify-between bg-[#F8F9FA]">
          <Select value={rfq.status} onValueChange={v => onStatusChange(rfq.id, v)}>
            <SelectTrigger className="w-[180px] rounded-none border-[#DEE2E6]" data-testid="rfq-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="reviewed">Reviewed</SelectItem>
              <SelectItem value="quoted">Quoted</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-[#DEE2E6] text-sm text-[#495057] hover:bg-white transition-colors duration-150"
            style={{ fontFamily: 'var(--font-body)', fontWeight: 500 }}
            data-testid="rfq-detail-close"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [rfqs, setRfqs] = useState([]);
  const [products, setProducts] = useState([]);
  const [rfqFilter, setRfqFilter] = useState('all');
  const [selectedRfq, setSelectedRfq] = useState(null);
  const [editProduct, setEditProduct] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const authHeaders = useCallback(() => ({
    withCredentials: true,
    headers: user?.token ? { Authorization: `Bearer ${user.token}` } : {},
  }), [user]);

  const fetchData = useCallback(async () => {
    setRefreshing(true);
    try {
      const [statsRes, rfqRes, prodRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, authHeaders()),
        axios.get(`${API}/rfq?status=${rfqFilter}`, authHeaders()),
        axios.get(`${API}/products`),
      ]);
      setStats(statsRes.data);
      setRfqs(rfqRes.data);
      setProducts(prodRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setRefreshing(false);
    }
  }, [authHeaders, rfqFilter, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStatusChange = async (rfqId, newStatus) => {
    try {
      await axios.put(`${API}/rfq/${rfqId}`, { status: newStatus }, authHeaders());
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
      if (selectedRfq?.id === rfqId) {
        setSelectedRfq(prev => ({ ...prev, status: newStatus }));
      }
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteRfq = async (rfqId) => {
    if (!window.confirm('Delete this RFQ?')) return;
    try {
      await axios.delete(`${API}/rfq/${rfqId}`, authHeaders());
      toast.success('RFQ deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleProductUpdate = async (productId) => {
    if (!editProduct) return;
    try {
      await axios.put(`${API}/products/${productId}`, editProduct, authHeaders());
      toast.success('Product updated');
      setEditProduct(null);
      fetchData();
    } catch {
      toast.error('Failed to update product');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <header className="bg-white border-b border-[#DEE2E6] px-6 md:px-12 h-14 flex items-center justify-between" data-testid="admin-header">
        <div className="flex items-center gap-3">
          <FlaskConical className="w-4 h-4 text-[#0A7C84]" strokeWidth={1.5} />
          <span className="text-sm tracking-tight text-[#0A1128]" style={{ fontFamily: 'var(--font-heading)', fontWeight: 700 }}>
            BIOLYN
          </span>
          <span className="text-[10px] tracking-widest text-[#A0AAB2] uppercase" style={{ fontFamily: 'var(--font-mono)' }}>
            Admin
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-xs text-[#6C757D]" style={{ fontFamily: 'var(--font-mono)' }}>
            {user?.email}
          </span>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 text-xs text-[#6C757D] hover:text-[#0A1128] transition-colors duration-150"
            style={{ fontFamily: 'var(--font-body)' }}
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
            Logout
          </button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-[#DEE2E6] bg-white mb-8" data-testid="admin-stats">
            <StatCard label="Total RFQs" value={stats.total_rfqs} icon={FileText} />
            <StatCard label="New" value={stats.new_rfqs} icon={BarChart3} />
            <StatCard label="Quoted" value={stats.quoted_rfqs} icon={ArrowRight} />
            <StatCard label="Products" value={stats.total_products} icon={Package} />
          </div>
        )}

        <Tabs defaultValue="inquiries" className="w-full">
          <div className="flex items-center justify-between mb-6">
            <TabsList className="rounded-none bg-white border border-[#DEE2E6] p-0 h-auto">
              <TabsTrigger value="inquiries" className="rounded-none data-[state=active]:bg-[#0A1128] data-[state=active]:text-white px-6 py-2.5 text-sm" style={{ fontFamily: 'var(--font-body)', fontWeight: 500 }} data-testid="tab-inquiries">
                Inquiries
              </TabsTrigger>
              <TabsTrigger value="products" className="rounded-none data-[state=active]:bg-[#0A1128] data-[state=active]:text-white px-6 py-2.5 text-sm" style={{ fontFamily: 'var(--font-body)', fontWeight: 500 }} data-testid="tab-products">
                Products
              </TabsTrigger>
            </TabsList>
            <button
              onClick={fetchData}
              disabled={refreshing}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-[#DEE2E6] text-xs text-[#6C757D] hover:bg-white transition-colors duration-150"
              style={{ fontFamily: 'var(--font-mono)' }}
              data-testid="admin-refresh-btn"
            >
              <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} strokeWidth={1.5} />
              Refresh
            </button>
          </div>

          {/* Inquiries Tab */}
          <TabsContent value="inquiries">
            {/* Filter */}
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs text-[#A0AAB2]" style={{ fontFamily: 'var(--font-mono)' }}>Filter:</span>
              {['all', 'new', 'reviewed', 'quoted', 'closed'].map(status => (
                <button
                  key={status}
                  onClick={() => setRfqFilter(status)}
                  className={`px-3 py-1 text-xs border transition-colors duration-150 ${rfqFilter === status ? 'bg-[#0A1128] text-white border-[#0A1128]' : 'border-[#DEE2E6] text-[#6C757D] hover:bg-white'}`}
                  style={{ fontFamily: 'var(--font-mono)' }}
                  data-testid={`rfq-filter-${status}`}
                >
                  {status}
                </button>
              ))}
            </div>

            <div className="bg-white border border-[#DEE2E6]">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-[#DEE2E6] hover:bg-transparent">
                    <TableHead className="text-[10px] tracking-widest uppercase" style={{ fontFamily: 'var(--font-mono)' }}>Company</TableHead>
                    <TableHead className="text-[10px] tracking-widest uppercase" style={{ fontFamily: 'var(--font-mono)' }}>Contact</TableHead>
                    <TableHead className="text-[10px] tracking-widest uppercase" style={{ fontFamily: 'var(--font-mono)' }}>Product</TableHead>
                    <TableHead className="text-[10px] tracking-widest uppercase" style={{ fontFamily: 'var(--font-mono)' }}>Volume</TableHead>
                    <TableHead className="text-[10px] tracking-widest uppercase" style={{ fontFamily: 'var(--font-mono)' }}>Status</TableHead>
                    <TableHead className="text-[10px] tracking-widest uppercase" style={{ fontFamily: 'var(--font-mono)' }}>Date</TableHead>
                    <TableHead className="text-[10px] tracking-widest uppercase text-right" style={{ fontFamily: 'var(--font-mono)' }}>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rfqs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12 text-sm text-[#A0AAB2]" style={{ fontFamily: 'var(--font-body)' }}>
                        No inquiries found
                      </TableCell>
                    </TableRow>
                  ) : (
                    rfqs.map(rfq => (
                      <TableRow key={rfq.id} className="border-b border-[#E9ECEF]" data-testid={`rfq-row-${rfq.id}`}>
                        <TableCell className="text-sm text-[#0A1128]" style={{ fontFamily: 'var(--font-body)', fontWeight: 500 }}>
                          {rfq.company_name}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-[#0A1128]" style={{ fontFamily: 'var(--font-body)' }}>{rfq.contact_name}</div>
                          <div className="text-xs text-[#A0AAB2]" style={{ fontFamily: 'var(--font-mono)' }}>{rfq.email}</div>
                        </TableCell>
                        <TableCell className="text-sm text-[#495057]" style={{ fontFamily: 'var(--font-body)' }}>{rfq.product_interest}</TableCell>
                        <TableCell className="text-xs text-[#495057]" style={{ fontFamily: 'var(--font-mono)' }}>{rfq.volume_required}</TableCell>
                        <TableCell>
                          <Badge className={`rounded-none border text-[10px] ${statusColors[rfq.status] || statusColors.new}`} variant="outline" style={{ fontFamily: 'var(--font-mono)' }}>
                            {rfq.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-[#A0AAB2]" style={{ fontFamily: 'var(--font-mono)' }}>
                          {rfq.created_at ? new Date(rfq.created_at).toLocaleDateString() : 'N/A'}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger className="p-1 hover:bg-[#F8F9FA] transition-colors duration-150" data-testid={`rfq-actions-${rfq.id}`}>
                              <MoreHorizontal className="w-4 h-4 text-[#A0AAB2]" strokeWidth={1.5} />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="rounded-none" align="end">
                              <DropdownMenuItem onClick={() => setSelectedRfq(rfq)} className="rounded-none text-sm cursor-pointer">
                                <Eye className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} /> View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleStatusChange(rfq.id, 'reviewed')} className="rounded-none text-sm cursor-pointer">
                                <ArrowRight className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} /> Mark Reviewed
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleStatusChange(rfq.id, 'quoted')} className="rounded-none text-sm cursor-pointer">
                                <FileText className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} /> Mark Quoted
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDeleteRfq(rfq.id)} className="rounded-none text-sm text-red-600 cursor-pointer">
                                <Trash2 className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <div className="space-y-4">
              {products.map(product => (
                <div key={product.id} className="bg-white border border-[#DEE2E6] p-6" data-testid={`admin-product-${product.slug}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg tracking-tight text-[#0A1128] mb-1" style={{ fontFamily: 'var(--font-heading)', fontWeight: 700 }}>
                        {product.name}
                      </h3>
                      <p className="text-sm text-[#6C757D]" style={{ fontFamily: 'var(--font-body)' }}>{product.tagline}</p>
                    </div>
                    <Badge className={`rounded-none border ${product.status === 'active' ? 'bg-green-100 text-green-800 border-green-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`} variant="outline" style={{ fontFamily: 'var(--font-mono)' }}>
                      {product.status}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-3 md:grid-cols-6 gap-4 mb-4">
                    {[
                      { label: 'CAS', value: product.cas_number },
                      { label: 'Weight', value: product.molecular_weight },
                      { label: 'Formula', value: product.molecular_formula },
                      { label: 'Purity', value: product.purity },
                      { label: 'Form', value: product.form },
                      { label: 'Storage', value: product.storage },
                    ].map(s => (
                      <div key={s.label}>
                        <span className="text-[10px] tracking-widest uppercase text-[#A0AAB2] block mb-0.5" style={{ fontFamily: 'var(--font-mono)' }}>{s.label}</span>
                        <span className="text-xs text-[#0A1128]" style={{ fontFamily: 'var(--font-mono)', fontWeight: 500 }}>{s.value}</span>
                      </div>
                    ))}
                  </div>

                  {editProduct && editProduct._editing === product.id ? (
                    <div className="border-t border-[#DEE2E6] pt-4 mt-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <Label className="text-xs text-[#6C757D] mb-1 block" style={{ fontFamily: 'var(--font-mono)' }}>Description</Label>
                          <textarea
                            className="w-full px-3 py-2 border border-[#DEE2E6] bg-[#F8F9FA] text-sm min-h-[80px] resize-y"
                            style={{ fontFamily: 'var(--font-body)' }}
                            value={editProduct.description || product.description}
                            onChange={e => setEditProduct(prev => ({ ...prev, description: e.target.value }))}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-[#6C757D] mb-1 block" style={{ fontFamily: 'var(--font-mono)' }}>Tagline</Label>
                          <Input
                            className="rounded-none border-[#DEE2E6] bg-[#F8F9FA]"
                            value={editProduct.tagline || product.tagline}
                            onChange={e => setEditProduct(prev => ({ ...prev, tagline: e.target.value }))}
                          />
                          <Label className="text-xs text-[#6C757D] mb-1 block mt-3" style={{ fontFamily: 'var(--font-mono)' }}>Purity</Label>
                          <Input
                            className="rounded-none border-[#DEE2E6] bg-[#F8F9FA]"
                            value={editProduct.purity || product.purity}
                            onChange={e => setEditProduct(prev => ({ ...prev, purity: e.target.value }))}
                          />
                          <Label className="text-xs text-[#6C757D] mb-1 block mt-3" style={{ fontFamily: 'var(--font-mono)' }}>Status</Label>
                          <Select value={editProduct.status || product.status} onValueChange={v => setEditProduct(prev => ({ ...prev, status: v }))}>
                            <SelectTrigger className="rounded-none border-[#DEE2E6] bg-[#F8F9FA]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="rounded-none">
                              <SelectItem value="active">Active</SelectItem>
                              <SelectItem value="inactive">Inactive</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleProductUpdate(product.id)}
                          className="px-4 py-2 bg-[#0A7C84] text-white text-sm hover:bg-[#0A1128] transition-colors duration-150"
                          style={{ fontFamily: 'var(--font-body)', fontWeight: 500 }}
                          data-testid={`save-product-${product.slug}`}
                        >
                          Save Changes
                        </button>
                        <button
                          onClick={() => setEditProduct(null)}
                          className="px-4 py-2 border border-[#DEE2E6] text-sm text-[#495057] hover:bg-[#F8F9FA] transition-colors duration-150"
                          style={{ fontFamily: 'var(--font-body)', fontWeight: 500 }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="border-t border-[#DEE2E6] pt-4 mt-4">
                      <button
                        onClick={() => setEditProduct({ _editing: product.id, description: product.description, tagline: product.tagline, purity: product.purity, status: product.status })}
                        className="px-4 py-2 border border-[#DEE2E6] text-sm text-[#495057] hover:bg-[#F8F9FA] transition-colors duration-150"
                        style={{ fontFamily: 'var(--font-body)', fontWeight: 500 }}
                        data-testid={`edit-product-${product.slug}`}
                      >
                        Edit Product
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* RFQ Detail Modal */}
      <RFQDetailModal
        rfq={selectedRfq}
        onClose={() => setSelectedRfq(null)}
        onStatusChange={handleStatusChange}
      />
    </div>
  );
}
